if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        // Gallery arrow navigation
        this.setupGalleryArrows();

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      setupGalleryArrows() {
        const prevArrow = this.querySelector('.gallery-arrow--prev');
        const nextArrow = this.querySelector('.gallery-arrow--next');

        if (!prevArrow || !nextArrow) return;

        // Click navigation
        prevArrow.addEventListener('click', () => this.navigateGallery('prev', prevArrow));
        nextArrow.addEventListener('click', () => this.navigateGallery('next', nextArrow));

        // Keyboard navigation: ArrowLeft / ArrowRight moves slides; focus stays on the arrow
        [prevArrow, nextArrow].forEach((arrow) => {
          arrow.addEventListener('keydown', (e) => {
            if (e.key === 'ArrowLeft') {
              e.preventDefault();
              this.navigateGallery('prev', prevArrow);
            } else if (e.key === 'ArrowRight') {
              e.preventDefault();
              this.navigateGallery('next', nextArrow);
            }
          });
        });

        // Focus trap: Tab cycles focus between the two arrow buttons
        prevArrow.addEventListener('keydown', (e) => {
          if (e.key === 'Tab' && e.shiftKey) {
            e.preventDefault();
            nextArrow.focus();
          }
        });

        nextArrow.addEventListener('keydown', (e) => {
          if (e.key === 'Tab' && !e.shiftKey) {
            e.preventDefault();
            prevArrow.focus();
          }
        });
      }

      navigateGallery(direction, focusTarget) {
        const mediaItems = this.elements.viewer.querySelectorAll('.product__media-item');
        const currentActive = this.elements.viewer.querySelector('.product__media-item.is-active');

        if (!currentActive || mediaItems.length <= 1) return;

        let currentIndex = Array.from(mediaItems).indexOf(currentActive);
        let newIndex;

        if (direction === 'prev') {
          newIndex = currentIndex === 0 ? mediaItems.length - 1 : currentIndex - 1;
        } else {
          newIndex = currentIndex === mediaItems.length - 1 ? 0 : currentIndex + 1;
        }

        const newMediaId = mediaItems[newIndex].dataset.mediaId;
        this.setActiveMedia(newMediaId, false);

        // Return focus to the triggering arrow button so keyboard users aren't lost
        if (focusTarget) focusTarget.focus();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement?.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        const expandBtn = this.querySelector('.gallery-expand');
        if (expandBtn) {
          expandBtn.setAttribute('data-media-id', mediaId);
        }

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          // The bundle variant picker (FOI-516) sets data-keep-scroll so a
          // selection made below the gallery doesn't yank the page back up.
          if (this.hasAttribute('data-keep-scroll')) return;
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}

// Product Modal Filmstrip Navigation
document.addEventListener('DOMContentLoaded', () => {
  const modal = document.querySelector('.product-media-modal');
  if (!modal) return;

  const modalContent = modal.querySelector('.product-media-modal__content');
  if (!modalContent) return;

  // Get all media items and thumbnails in modal
  const mediaItems = modal.querySelectorAll('.product-media-modal__media');
  const thumbnails = modal.querySelectorAll('.product-media-modal__thumbnail-item');
  const prevBtn = modal.querySelector('.modal-arrow--prev');
  const nextBtn = modal.querySelector('.modal-arrow--next');

  function getActiveIndex() {
    let activeIndex = 0;
    mediaItems.forEach((item, index) => {
      if (item.classList.contains('is-active')) {
        activeIndex = index;
      }
    });
    return activeIndex;
  }

  function setActiveMedia(index) {
    if (typeof window.pauseAllMedia === 'function') {
      window.pauseAllMedia();
    }

    mediaItems.forEach((item) => {
      item.classList.remove('is-active');
      item.style.display = 'none';
    });
    thumbnails.forEach((thumb) => {
      thumb.classList.remove('is-active');
      thumb.querySelector('button').removeAttribute('aria-current');
    });

    if (mediaItems[index]) {
      mediaItems[index].classList.add('is-active');
      mediaItems[index].style.display = '';
    }

    if (thumbnails[index]) {
      thumbnails[index].classList.add('is-active');
      thumbnails[index].querySelector('button').setAttribute('aria-current', 'true');
    }
  }

  function navigateModal(direction) {
    const currentIndex = getActiveIndex();
    let newIndex;

    if (direction === 'prev') {
      newIndex = currentIndex === 0 ? mediaItems.length - 1 : currentIndex - 1;
    } else {
      newIndex = currentIndex === mediaItems.length - 1 ? 0 : currentIndex + 1;
    }

    setActiveMedia(newIndex);
  }

  // Prevent pointerup from reaching ModalDialog's close handler
  const gallery = modal.querySelector('.product-media-modal__gallery');
  if (gallery) {
    gallery.addEventListener('pointerup', (e) => e.stopPropagation());
  }

  if (prevBtn) {
    prevBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      navigateModal('prev');
    });
  }

  if (nextBtn) {
    nextBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      navigateModal('next');
    });
  }

  thumbnails.forEach((thumb, index) => {
    thumb.querySelector('button').addEventListener('click', (e) => {
      e.stopPropagation();
      setActiveMedia(index);
    });
  });

  // Keyboard navigation
  modalContent.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowLeft') {
      navigateModal('prev');
    } else if (e.key === 'ArrowRight') {
      navigateModal('next');
    } else if (e.key === 'Escape') {
      const closeBtn = modal.querySelector('.product-media-modal__toggle');
      if (closeBtn) {
        closeBtn.click();
      }
    }
  });
});
